import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { PromiseWithServerResult, Zero } from "@rocicorp/zero";
import { createId } from "@corpuscomplete/core/utils";
import { PlannedMeal } from "@corpuscomplete/core/nutrition/planned-meal";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { CurrentActor } from "../../../actor";
import { MealScheduleUI } from "../../meal-schedule";

export namespace PlannedMealCalendar {
  export type Context = {
    scopeId: string;
    focusedDate: Temporal.PlainDate;
    timeZone: string;
  };

  const DataContext = createContext<Context | null>(null);

  export function DataProvider({
    children,
    scopeId,
    focusedDate,
    timeZone,
  }: PropsWithChildren<Context>) {
    const focusedDateKey = focusedDate.toString();
    const value = useMemo(
      () => ({ scopeId, focusedDate, timeZone }),
      [scopeId, focusedDateKey, timeZone],
    );
    return <DataContext value={value}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error("PlannedMealCalendar.useContext must be used within DataProvider");
    }
    return context;
  }

  export type WeeklyEvents = {
    previous: PlannedMeal.Info[];
    current: PlannedMeal.Info[];
    next: PlannedMeal.Info[];
    unscheduledDrafts: PlannedMeal.Info[];
    all: PlannedMeal.Info[];
  };

  export function useWeeklyEvents(): WeeklyEvents {
    const { scopeId, focusedDate, timeZone } = useContext();
    const weekStart = useMemo(() => getSundayWeekStart(focusedDate), [focusedDate]);
    const previousWeekStart = useMemo(() => weekStart.subtract({ days: 7 }), [weekStart]);
    const nextWeekStart = useMemo(() => weekStart.add({ days: 7 }), [weekStart]);

    const [previous] = useQuery(
      queries.plannedMeal.eventsInDateRange(rangeQuery(scopeId, previousWeekStart, timeZone)),
    );
    const [current] = useQuery(
      queries.plannedMeal.eventsInDateRange(rangeQuery(scopeId, weekStart, timeZone)),
    );
    const [next] = useQuery(
      queries.plannedMeal.eventsInDateRange(rangeQuery(scopeId, nextWeekStart, timeZone)),
    );
    const [unscheduledDrafts] = useQuery(
      queries.plannedMeal.withoutEventsByScope({ scopeId, limit: 50, cursor: undefined }),
    );

    return useMemo(
      () => ({
        previous: previous.map(PlannedMeal.asInfo),
        current: current.map(PlannedMeal.asInfo),
        next: next.map(PlannedMeal.asInfo),
        unscheduledDrafts: unscheduledDrafts.map(PlannedMeal.asInfo),
        all: [...previous, ...current, ...next, ...unscheduledDrafts].map(PlannedMeal.asInfo),
      }),
      [previous, current, next, unscheduledDrafts],
    );
  }

  export function useTypeLabelOptions(): string[] {
    const { scopeId } = useContext();
    return MealScheduleUI.useTypeLabelOptions(scopeId);
  }

  export function useLoadingState(): { isLoading: boolean; isWarmingAdjacentWeeks: boolean } {
    const { scopeId, focusedDate, timeZone } = useContext();
    const weekStart = useMemo(() => getSundayWeekStart(focusedDate), [focusedDate]);
    const previousWeekStart = useMemo(() => weekStart.subtract({ days: 7 }), [weekStart]);
    const nextWeekStart = useMemo(() => weekStart.add({ days: 7 }), [weekStart]);
    const [, previousDetails] = useQuery(
      queries.plannedMeal.eventsInDateRange(rangeQuery(scopeId, previousWeekStart, timeZone)),
    );
    const [, currentDetails] = useQuery(
      queries.plannedMeal.eventsInDateRange(rangeQuery(scopeId, weekStart, timeZone)),
    );
    const [, nextDetails] = useQuery(
      queries.plannedMeal.eventsInDateRange(rangeQuery(scopeId, nextWeekStart, timeZone)),
    );
    const [, unscheduledDetails] = useQuery(
      queries.plannedMeal.withoutEventsByScope({ scopeId, limit: 50, cursor: undefined }),
    );
    const [, scheduleDetails] = MealScheduleUI.useByScope(scopeId);

    return {
      isLoading:
        currentDetails.type === "unknown" ||
        unscheduledDetails.type === "unknown" ||
        scheduleDetails.type === "unknown",
      isWarmingAdjacentWeeks: previousDetails.type === "unknown" || nextDetails.type === "unknown",
    };
  }

  export type Filters = {
    statuses: readonly PlannedMeal.Status[] | null;
  };

  export type CalendarEvent = PlannedMeal.Info["events"][number] & {
    plannedMeal: PlannedMeal.Info;
    type: "event";
    timeStart: Temporal.Instant;
    timeEnd: Temporal.Instant;
  };

  export type CalendarEntry = {
    instance: CalendarEvent;
    date: Temporal.PlainDate;
    kind: "event";
    currentUserAssignment: PlannedMeal.Info["assignments"][number];
    visiblePlate: PlannedMeal.Info["plates"][number];
    assignmentCount: number;
    currentUserItemCount: number;
    currentUserDescriptorCount: number;
    effectiveTimeAt: Temporal.Instant;
  };

  export function entryKey(entry: CalendarEntry): string {
    return entryKeyFromParts(entry.instance.id, entry.date);
  }

  export function entryKeyFromParts(instanceId: string, date: Temporal.PlainDate): string {
    return `${instanceId}:${date.toString()}`;
  }

  export function findEntryByKey(
    rows: readonly DateRow[],
    key: string | null,
  ): CalendarEntry | null {
    if (!key) return null;
    return rows.flatMap((row) => row.entries).find((entry) => entryKey(entry) === key) ?? null;
  }

  export function unscheduledDraftKey(meal: Pick<PlannedMeal.Info, "id">): string {
    return `draft:${meal.id}`;
  }

  export function findUnscheduledDraftByKey(
    drafts: readonly PlannedMeal.Info[],
    key: string | null,
  ): PlannedMeal.Info | null {
    if (!key?.startsWith("draft:")) return null;
    return drafts.find((meal) => unscheduledDraftKey(meal) === key) ?? null;
  }

  export type DateRow = {
    date: Temporal.PlainDate;
    intents: CalendarEntry[];
    events: CalendarEntry[];
    entries: CalendarEntry[];
  };

  export type CalendarViewModel = {
    weekStart: Temporal.PlainDate;
    dates: Temporal.PlainDate[];
    rows: DateRow[];
    unscheduledDrafts: PlannedMeal.Info[];
    weekStrip: WeekStripDay[];
  };

  export function useCalendarViewModel(filters: Filters = { statuses: null }): CalendarViewModel {
    const { focusedDate, timeZone } = useContext();
    const actor = CurrentActor.use();
    const { current, unscheduledDrafts } = useWeeklyEvents();
    return useMemo(
      () =>
        buildCalendarViewModel({
          focusedDate,
          timeZone,
          currentUserId: actor.userId,
          instances: current,
          unscheduledDrafts,
          filters,
        }),
      [actor.userId, current, unscheduledDrafts, filters, focusedDate, timeZone],
    );
  }

  export type QuickCreateDefaults =
    | { disabled: true; reason: "missing-labels" | "missing-members" }
    | { disabled: false; typeLabel: string; payload: PlannedMeal.CreateDraft };

  export function buildStandaloneQuickCreateInput(args: {
    id: string;
    scopeId: string;
    userId: string;
    typeLabel: string;
    date: Temporal.PlainDate;
    timeZone: string;
    plateId: string;
    eventId: string;
  }): PlannedMeal.CreateDraft {
    const eventId = args.eventId || createId();
    return {
      id: args.id,
      scopeId: args.scopeId,
      typeLabel: args.typeLabel,
      name: "",
      events: [
        {
          id: eventId,
          timeAt: args.date
            .toZonedDateTime({
              timeZone: args.timeZone,
              plainTime: Temporal.PlainTime.from("12:00"),
            })
            .toInstant(),
          timeZone: args.timeZone,
          assignments: [{ id: createId(), plateId: args.plateId }],
        },
      ],
      plates: [
        {
          id: args.plateId,
          userId: args.userId,
          nutrientTargets: null,
          items: [],
          itemDescriptors: [],
        },
      ],
    };
  }

  export function useQuickCreateDefaults(input: {
    date: Temporal.PlainDate;
    memberUserIds?: readonly string[];
  }): QuickCreateDefaults {
    const { scopeId, timeZone } = useContext();
    const actor = CurrentActor.use();
    const { all } = useWeeklyEvents();
    const typeLabelOptions = useTypeLabelOptions();

    return useMemo(
      () =>
        buildQuickCreateDefaults({
          scopeId,
          date: input.date,
          timeZone,
          typeLabelOptions,
          instances: all,
          currentUserId: actor.userId,
          memberUserIds: input.memberUserIds,
        }),
      [actor.userId, all, typeLabelOptions, input.date, input.memberUserIds, scopeId, timeZone],
    );
  }

  export type Mutations = {
    createDraft: (payload: PlannedMeal.CreateDraft) => PromiseWithServerResult;
  };
  export function useMutations(): Mutations {
    const z = useZero();
    return useMemo(
      () => ({
        createDraft: (payload) => z.mutate(mutators.plannedMeal.createDraft(payload)),
      }),
      [z],
    );
  }

  export type EntryMutations = {
    rename: (name: string) => PromiseWithServerResult;
    moveEvent: (timeAt: Temporal.Instant) => PromiseWithServerResult;
    publish: () => PromiseWithServerResult;
    unpublish: () => PromiseWithServerResult;
    deleteDraft: () => PromiseWithServerResult;
    scheduleIntent: (input: {
      eventInstanceId: string;
      timeAt: Temporal.Instant;
    }) => PromiseWithServerResult;
  };

  export function useEntryMutations(entry: CalendarEntry): EntryMutations {
    const z = useZero();
    const mealId = entry.instance.plannedMeal.id;
    const eventId = entry.instance.id;

    return useMemo(
      () => ({
        rename: (name) => z.mutate(mutators.plannedMeal.editDraft({ id: mealId, name })),
        moveEvent: (timeAt) =>
          z.mutate(mutators.plannedMeal.editEvent({ mealId, id: eventId, timeAt })),
        publish: () => z.mutate(mutators.plannedMeal.publish({ id: mealId })),
        unpublish: () => z.mutate(mutators.plannedMeal.unpublish({ id: mealId })),
        deleteDraft: () => z.mutate(mutators.plannedMeal.deleteDraft({ id: mealId })),
        scheduleIntent: () => noopMutationResult(),
      }),
      [eventId, mealId, z],
    );
  }

  export function quickCreateDisabledReason(input: {
    readOnly: boolean;
    defaults: QuickCreateDefaults;
  }): string | null {
    if (input.readOnly) {
      return "You do not have write access to this scope";
    }
    if (!input.defaults.disabled) {
      return null;
    }
    return input.defaults.reason === "missing-labels"
      ? "Create a meal schedule before adding meals"
      : "Members are still loading";
  }

  export function defaultScheduleTime(_entry: CalendarEntry): Temporal.PlainTime {
    return Temporal.PlainTime.from("12:00");
  }

  export function instantFromLocalDateTime(input: {
    date: Temporal.PlainDate | string;
    time: Temporal.PlainTime | string;
    timeZone: string;
  }): Temporal.Instant {
    const date = typeof input.date === "string" ? Temporal.PlainDate.from(input.date) : input.date;
    const time = typeof input.time === "string" ? Temporal.PlainTime.from(input.time) : input.time;
    return date.toZonedDateTime({ timeZone: input.timeZone, plainTime: time }).toInstant();
  }

  export function scheduleIntentValidationMessage(): string | null {
    return null;
  }

  export function publishBlockingReasons(entry: CalendarEntry): string[] {
    const meal = entry.instance.plannedMeal;
    const reasons: string[] = [];
    if (!meal.name.trim()) reasons.push("Name is required before publishing.");
    if (meal.plates.length === 0) reasons.push("At least one plate is required before publishing.");
    if (meal.events.length === 0) reasons.push("At least one event is required before publishing.");
    if (meal.events.some((event) => event.assignments.length === 0)) {
      reasons.push("Every event needs at least one assignment.");
    }
    if (
      meal.plates.some(
        (plate) => !meal.assignments.some((assignment) => assignment.plateId === plate.id),
      )
    ) {
      reasons.push("Every plate needs at least one assignment.");
    }
    if (meal.plates.some((plate) => plate.items.length === 0)) {
      reasons.push("Every plate needs at least one item.");
    }
    if (meal.plates.some((plate) => plate.itemDescriptors.length > 0)) {
      reasons.push("Resolve descriptors before publishing.");
    }
    return reasons;
  }

  export function watchMutation(
    result: PromiseWithServerResult,
    setError: (message: string | null) => void,
    fallbackMessage: string,
  ): void {
    void result.client
      .then((clientResult) => {
        if (clientResult.type === "error") {
          setError(clientResult.error.message);
        }
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : fallbackMessage);
      });
  }

  export function noopMutationResult(): PromiseWithServerResult {
    const success = Promise.resolve({ type: "success" as const });
    return { client: success, server: success };
  }

  export function preload(zero: Zero, input: Context): void {
    const weekStart = getSundayWeekStart(input.focusedDate);
    void zero.run(
      queries.plannedMeal.eventsInDateRange(rangeQuery(input.scopeId, weekStart, input.timeZone)),
    );
    void zero.run(
      queries.plannedMeal.withoutEventsByScope({
        scopeId: input.scopeId,
        limit: 50,
        cursor: undefined,
      }),
    );
    void zero.run(queries.mealSchedule.byScope({ scopeId: input.scopeId }));
  }

  function getSundayWeekStart(date: Temporal.PlainDate): Temporal.PlainDate {
    return date.subtract({ days: date.dayOfWeek % 7 });
  }

  export function buildCalendarViewModel(input: {
    focusedDate: Temporal.PlainDate;
    timeZone: string;
    currentUserId: string;
    instances: readonly PlannedMeal.Info[];
    unscheduledDrafts?: readonly PlannedMeal.Info[];
    filters?: Filters;
  }): CalendarViewModel {
    const weekStart = getSundayWeekStart(input.focusedDate);
    const dates = Array.from({ length: 7 }, (_, day) => weekStart.add({ days: day }));
    const statuses = new Set(input.filters?.statuses ?? []);
    const dateSet = new Set(dates.map((date) => date.toString()));
    const entries = input.instances.flatMap((meal) => {
      if (statuses.size > 0 && !statuses.has(meal.status)) return [];
      return meal.events.flatMap((event) => {
        const assignment = event.assignments.find(
          (candidate) => candidate.userId === input.currentUserId,
        );
        if (!assignment) return [];
        const visiblePlate = meal.plates.find((plate) => plate.id === assignment.plateId);
        if (!visiblePlate) return [];
        const date = event.timeAt.toZonedDateTimeISO(input.timeZone).toPlainDate();
        if (!dateSet.has(date.toString())) return [];
        const instance = eventAsEntryInstance(meal, event);
        return [
          {
            instance,
            date,
            kind: "event" as const,
            currentUserAssignment: assignment,
            visiblePlate,
            assignmentCount: event.assignments.length,
            currentUserItemCount: visiblePlate.items.length,
            currentUserDescriptorCount: visiblePlate.itemDescriptors.length,
            effectiveTimeAt: event.timeAt,
          },
        ];
      });
    });

    const rows = dates.map((date) => {
      const onDate = entries.filter((entry) => Temporal.PlainDate.compare(entry.date, date) === 0);
      const events = onDate.sort((left, right) =>
        Temporal.Instant.compare(left.effectiveTimeAt, right.effectiveTimeAt),
      );
      return { date, intents: [], events, entries: events };
    });

    return {
      weekStart,
      dates,
      rows,
      unscheduledDrafts: (input.unscheduledDrafts ?? []).filter(
        (meal) => statuses.size === 0 || statuses.has(meal.status),
      ),
      weekStrip: rows.map((row) => ({
        date: row.date,
        hasIntents: false,
        hasEvents: row.events.length > 0,
        count: row.entries.length,
      })),
    };
  }

  export type WeekStripDay = {
    date: Temporal.PlainDate;
    hasIntents: boolean;
    hasEvents: boolean;
    count: number;
  };

  export function buildQuickCreateDefaults(input: {
    scopeId: string;
    date: Temporal.PlainDate;
    timeZone: string;
    typeLabelOptions: readonly string[] | undefined;
    instances: readonly PlannedMeal.Info[];
    currentUserId: string;
    memberUserIds?: readonly string[];
  }): QuickCreateDefaults {
    if (!input.typeLabelOptions || input.typeLabelOptions.length === 0) {
      return { disabled: true, reason: "missing-labels" };
    }
    if (!input.memberUserIds || input.memberUserIds.length === 0) {
      return { disabled: true, reason: "missing-members" };
    }

    const usedLabels = new Set(
      input.instances.flatMap((meal) => {
        const hasEventOnDate = meal.events.some(
          (event) =>
            event.assignments.some((assignment) => assignment.userId === input.currentUserId) &&
            event.timeAt.toZonedDateTimeISO(input.timeZone).toPlainDate().equals(input.date),
        );
        return hasEventOnDate ? [meal.typeLabel] : [];
      }),
    );
    const typeLabel =
      input.typeLabelOptions.find((candidate) => !usedLabels.has(candidate)) ??
      input.typeLabelOptions[0];
    if (!typeLabel) {
      return { disabled: true, reason: "missing-labels" };
    }
    const timeAt = input.date
      .toZonedDateTime({
        timeZone: input.timeZone,
        plainTime: Temporal.PlainTime.from("12:00"),
      })
      .toInstant();
    const eventId = createId();
    const plates = input.memberUserIds.map((userId) => ({
      id: createId(),
      userId,
      nutrientTargets: null,
      items: [],
      itemDescriptors: [],
    }));

    return {
      disabled: false,
      typeLabel,
      payload: {
        id: createId(),
        scopeId: input.scopeId,
        typeLabel,
        name: "",
        events: [
          {
            id: eventId,
            timeAt,
            timeZone: input.timeZone,
            assignments: plates.map((plate) => ({ id: createId(), plateId: plate.id })),
          },
        ],
        plates,
      },
    };
  }

  function eventAsEntryInstance(
    meal: PlannedMeal.Info,
    event: PlannedMeal.Info["events"][number],
  ): CalendarEvent {
    return {
      ...event,
      plannedMeal: meal,
      type: "event",
      timeStart: event.timeAt,
      timeEnd: event.timeAt,
    };
  }

  function rangeQuery(scopeId: string, weekStart: Temporal.PlainDate, timeZone: string) {
    return {
      scopeId,
      dateRange: {
        dateStart: weekStart,
        dateEnd: weekStart.add({ days: 6 }),
      },
      timeZone,
    };
  }
}
